import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import useGlobals from '../../../../Hooks/useGlobals';
import { settingsService } from '../../../../Services/settingsService';
import { Button } from '../../../Utility/Button';
import Select from '../../../Utility/FormElements/Select';
import { SocketContext } from '../../../../SocketContext';
import { Spinner } from '../../../Utility/Spinner';
import * as semver from 'semver';
import { Badge } from '../../../Utility/Badge';
import { Check, CheckCircle, Pencil, WarningCircle } from '@phosphor-icons/react';
import { setModal } from '../../../../Store/Actions/modalActions';
import { useDispatch } from 'react-redux';
import useSettings from '../../../../Hooks/useSettings';
import TextareaAutosize from 'react-textarea-autosize';

function System() {
    const { i18n, settings, userSettings, requestClient } = useGlobals();
    const { socket } = useContext(SocketContext);
    const [version, setVersion] = useState<string | null>(null);
    const [availableVersions, setAvailableVersions] = useState<string[]>([]);
    const [latestVersion, setLatestVersion] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isUpdating, setIsUpdating] = useState(false);
    const [releaseChannels, setReleaseChannels] = useState<string[]>([]);
    const [licenseKeyDisplay, setLicenseKeyDisplay] = useState<string>('');
    const isSavingRef = useRef(false);
    const dispatch = useDispatch();
    const { SettingsActions, Currentsettings } = useSettings();

    const openReleaseModal = useCallback(() => {
        const content = (
            <div className="grid grid-cols-2 gap-3">
                {releaseChannels.map(
                    (releaseChannel) =>
                        ((userSettings.isAvency && releaseChannel === 'alpha') || releaseChannel !== 'alpha') && (
                            <div className="flex" key={releaseChannel}>
                                <div
                                    className={`relative border-2 rounded-xl p-4 cursor-pointer flex-1 flex flex-col ${
                                        settings.releaseChannel === releaseChannel
                                            ? 'border-primary'
                                            : 'border-layout-borderColor hover:border-primary'
                                    }`}
                                    onClick={() => {
                                        SettingsActions.saveReleaseChannel(releaseChannel);
                                        dispatch(
                                            setModal({
                                                content: '',
                                                title: '',
                                                showCloseButton: true,
                                                modalSize: 'medium',
                                                state: 'closed',
                                                closeOnAction: true,
                                                showRightButton: true,
                                                showLeftButton: true,
                                            }),
                                        );
                                    }}
                                >
                                    {settings.releaseChannel === releaseChannel && (
                                        <div className="h-5 w-5 p-1 rounded-full bg-primary absolute text-layout-textColorOnPrimary top-4 right-4 flex items-center justify-center">
                                            <Check size={16} />
                                        </div>
                                    )}
                                    <h6 className="mb-3">
                                        {i18n.translate({
                                            id: 'releaseChannel.' + releaseChannel + '.headline',
                                            sourceName: 'System',
                                        })}
                                    </h6>
                                    <div>
                                        {i18n.translate({
                                            id: 'releaseChannel.' + releaseChannel + '.info',
                                            sourceName: 'System',
                                        })}
                                    </div>
                                </div>
                            </div>
                        ),
                )}
            </div>
        );
        dispatch(
            setModal({
                content: content,
                title: i18n.translate({
                    id: 'info.release',
                    sourceName: 'System',
                }),
                showCloseButton: true,
                modalSize: 'medium',
                state: 'open',
                closeOnAction: true,
                showRightButton: false,
                showLeftButton: false,
            }),
        );
    }, [releaseChannels, dispatch, i18n, settings.releaseChannel, SettingsActions]);

    useEffect(() => {
        const fetchVersions = async () => {
            try {
                const timestamp = new Date().getTime();
                const response = await requestClient.get<{ stable: string[]; beta: string[]; alpha: string[] }>(
                    `https://versions.varios-ai.com/versions.json?timestamp=${timestamp}`,
                );

                const allVersions = [
                    ...(response?.data[settings.releaseChannel as 'stable' | 'beta' | 'alpha'] ?? []),
                    settings.version,
                ]
                    .filter((v, i, arr) => semver.valid(v) && arr.indexOf(v) === i)
                    .sort((a, b) => semver.rcompare(a, b));
                setLatestVersion(allVersions[0] ?? null);
                const filteredVersions = allVersions.filter((v) => v !== settings.version);

                setAvailableVersions(filteredVersions);
                setReleaseChannels(Object.keys(response.data));
                setVersion(filteredVersions[0] ?? null);
            } catch (error) {
                console.error('Error fetching available versions:', error);
            } finally {
                setIsLoading(false);
            }
        };

        const timer = setTimeout(fetchVersions, 1000);
        return () => clearTimeout(timer);
    }, [settings.version, requestClient, settings.releaseChannel]);

    const handleUpdateVersion = async () => {
        if (!version || !socket) return;

        try {
            setIsUpdating(true);
            const params = {
                type: 'systemStatus',
                systemStatus: 'updating',
            };

            socket.send(JSON.stringify(params));
            await settingsService.updateVersion(version);
        } catch (error) {
            console.error('Error updating version:', error);
        } finally {
            setIsUpdating(false);
            dispatch(
                setModal({
                    state: 'closed',
                    title: '',
                    content: '',
                    buttonRightText: null,
                    buttonRightAction: null,
                    buttonRightVariant: 'primary',
                    showRightButton: true,
                    buttonLeftText: null,
                    buttonLeftAction: null,
                    buttonLeftVariant: 'secondary',
                    showLeftButton: true,
                    modalSize: 'small',
                    closeOnAction: false,
                    showCloseButton: true,
                    closeFunction: null,
                    customButtons: [],
                }),
            );
        }
    };

    const isLatestVersion = settings.version === latestVersion;

    useEffect(() => {
        if (isSavingRef.current && licenseKeyDisplay) {
            const timer = setTimeout(() => {
                setLicenseKeyDisplay('');
                isSavingRef.current = false;
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [settings, licenseKeyDisplay]);

    return (
        <div className="w-full flex gap-5 flex-col">
            <h4>{i18n.translate({ id: 'title', sourceName: 'System' })}</h4>

            <div className="flex flex-col gap-3">
                <div className="flex gap-2">
                    <span className="font-semibold">
                        {i18n.translate({ id: 'info.status', sourceName: 'System' })}:
                    </span>
                    <span>{settings.systemStatus}</span>
                </div>

                <div className="flex gap-2">
                    <span className="font-semibold">
                        {i18n.translate({ id: 'info.version', sourceName: 'System' })}:
                    </span>
                    <span>{settings.version}</span>
                </div>

                <div className="flex gap-2 items-center">
                    <span className="font-semibold">
                        {i18n.translate({ id: 'info.release', sourceName: 'System' })}:
                    </span>
                    <span>
                        {i18n.translate({
                            id: 'releaseChannel.' + settings.releaseChannel + '.headline',
                            sourceName: 'System',
                        })}
                    </span>
                    {!isLoading && (
                        <div>
                            <Pencil size={16} onClick={openReleaseModal} />
                        </div>
                    )}
                </div>

                {settings.accountId && (
                    <div className="flex gap-2">
                        <span className="font-semibold">
                            {i18n.translate({ id: 'info.accountid', sourceName: 'System' })}:
                        </span>
                        <span>{settings.accountId}</span>
                    </div>
                )}
            </div>

            <label>
                <strong>{i18n.translate({ id: 'licenseKey', sourceName: 'Settings' })}</strong>
                <TextareaAutosize
                    className="bg-input-backgroundColor w-full p-3 border border-2 border-input-borderColor rounded-lg outline-none focus:border-primary transition-all duration-100"
                    value={licenseKeyDisplay}
                    onChange={(value) => {
                        const newValue = value.target.value;
                        setLicenseKeyDisplay(newValue);
                    }}
                    placeholder={licenseKeyDisplay === '' ? '••••••••' : ''}
                />
            </label>
            <div className="">
                <Button
                    onClick={() => {
                        if (licenseKeyDisplay) {
                            isSavingRef.current = true;
                        }
                        SettingsActions.handleUpdateSettings(licenseKeyDisplay || undefined);
                    }}
                >
                    {i18n.translate({ id: 'save', sourceName: 'Main' })}
                </Button>
            </div>

            {userSettings.isSuperAdmin && settings.selfUpdateActive && (
                <>
                    <h4>{i18n.translate({ id: 'update.title', sourceName: 'System' })}</h4>

                    <div className="flex flex-col gap-4">
                        <div className="flex items-center gap-3">
                            {isLoading ? (
                                <Spinner
                                    size="lg"
                                    text={i18n.translate({ id: 'update.checking', sourceName: 'System' })}
                                />
                            ) : (
                                <>
                                    <Badge variant={isLatestVersion ? 'success' : 'warning'} className="w-fit">
                                        <div className="flex gap-1 items-center">
                                            {isLatestVersion ? <CheckCircle size={16} /> : <WarningCircle size={16} />}
                                            {isLatestVersion
                                                ? i18n.translate({ id: 'update.latest', sourceName: 'System' })
                                                : i18n.translate({ id: 'update.available', sourceName: 'System' })}
                                        </div>
                                    </Badge>
                                </>
                            )}
                        </div>

                        {!isLoading && availableVersions.length > 0 && (
                            <div className="flex gap-3 items-center">
                                <Select
                                    value={version}
                                    onChange={(version) => setVersion(version)}
                                    classList="max-w-[200px]"
                                >
                                    {availableVersions.map((availableVersion) => (
                                        <option key={availableVersion} value={availableVersion}>
                                            {availableVersion}
                                        </option>
                                    ))}
                                </Select>
                                <Button
                                    onClick={() =>
                                        dispatch(
                                            setModal({
                                                title: i18n.translate({
                                                    id: 'update.confirm.title',
                                                    sourceName: 'System',
                                                }),
                                                content: i18n.translate({
                                                    id: 'update.confirm.text',
                                                    sourceName: 'System',
                                                }),

                                                buttonRightAction: handleUpdateVersion,
                                                buttonRightText: i18n.translate({
                                                    id: 'update.confirm.button',
                                                    sourceName: 'System',
                                                }),
                                                state: 'open',
                                                modalSize: 'small',
                                                showCloseButton: false,
                                                closeOnAction: true,
                                                buttonLeftText: i18n.translate({
                                                    id: 'update.cancel.button',
                                                    sourceName: 'System',
                                                }),
                                            }),
                                        )
                                    }
                                    className={isLatestVersion ? 'opacity-80' : ''}
                                    disabled={isUpdating}
                                >
                                    {isUpdating
                                        ? i18n.translate({ id: 'update.updating', sourceName: 'System' })
                                        : isLatestVersion
                                        ? i18n.translate({ id: 'update.downgrade', sourceName: 'System' })
                                        : i18n.translate({ id: 'update.button', sourceName: 'System' })}
                                </Button>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

export default System;
